import {
  type GitBranchSubject,
  type GitLinkOverrideView,
  type GitLinkSubjectKind,
  type GitTargetLinks,
} from '@apm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, GitBranch } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { CollapsibleSection } from '../../../components/ui/collapsible-section';
import { Skeleton } from '../../../components/ui/states';
import { copyText } from '../../../lib/clipboard';
import { displayDateTime } from '../../../lib/date';
import { errorMessage, isForbidden } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { type GitTargetRef, gitApi, gitKeys } from '../api';
import {
  changeRequestRefOfUrl,
  normalizeGitAsOf,
  shortSha,
  suggestedBranchName,
} from '../git-format';
import { GIT_DEVELOPMENT_SLOTS } from '../slots';
import { useProjectGitConnected } from '../use-project-git-connected';
import { GitChangeRequestMarks } from './git-change-request-list';
import { GitManualLinkForm } from './git-manual-link-form';
import { GitChangeRequestBadge, GitExternalLink, GitTimeNotice } from './git-marks';

/** Một thứ ở git mà việc này đang nối tới — khoá của một quyết định gắn / ẩn tay. */
interface Subject {
  readonly repositoryId: string;
  readonly kind: GitLinkSubjectKind;
  readonly ref: string;
}

const sameSubject = (override: GitLinkOverrideView, subject: Subject): boolean =>
  override.repositoryId === subject.repositoryId &&
  override.subjectKind === subject.kind &&
  override.subjectRef === subject.ref;

/** Việc này có ít nhất một liên kết hiệu lực tới git (nhánh, MR hay commit). */
const hasLinks = (data: GitTargetLinks): boolean =>
  data.branches.length > 0 || data.changeRequests.length > 0 || data.commitCount > 0;

/**
 * Khối **"Phát triển"** trong chi tiết task và issue — APM-GIT-e,
 * docs/plan-git-integration.md §8.2, §8.3: nhánh, MR (trạng thái + review + CI),
 * số commit; nút **Sao chép tên nhánh**, gắn link tay, ẩn liên kết nhầm.
 *
 * ## Quyền
 *
 * Người không có `git:read` **không thấy khối và không lời gọi nào được phát**:
 * hai query đều `enabled` theo quyền, và quyền đọc từ `['abilities']` vỏ ứng dụng
 * đã tải sẵn. Chi tiết task mở từ Board, Gantt, Việc của tôi, hộp thư… — hỏi
 * quyền ở đây thì mọi nơi ấy đều đúng mà không phải truyền gì xuống.
 *
 * ## Dự án chưa nối Git ⇒ không có khối (APM-GIT-q)
 *
 * Người dùng chốt 2026-10-12: dự án chưa nối Git thì không hiện phần Git nào ngoài
 * tab Git — kể cả nút **Sao chép tên nhánh**, thứ trước đây cố ý chạy cả khi chưa
 * nối kho (§8.3 đã sửa theo). "Đã nối" = có ít nhất một kho đang gắn
 * (`useProjectGitConnected`); lúc chưa biết thì không vẽ gì, để không nháy khung
 * rỗng.
 *
 * Ngoại lệ để không mất dữ liệu: việc **đã có** liên kết (tự nối, gắn tay, hay kho
 * đã gỡ sau đó — `GET …/links` không lọc kho đã gỡ) thì khối vẫn hiện, ở chế độ
 * **chỉ đọc**: danh sách nhánh / MR / commit và các mục đã ẩn, không nút sao chép,
 * không gắn / ẩn / gỡ, không chỗ cắm (Tạo nhánh / MR, chip bản phát hành, đối chiếu
 * tiêu chí). Không thêm lời gọi nào — khối vốn đã hỏi `…/links`.
 *
 * ## Nút sao chép
 *
 * Tên nhánh dựng bằng `buildBranchName` (contracts) từ cấu hình dự án; cấu hình
 * chưa tải xong, lỗi, hay dự án chưa lưu gì thì dùng định dạng mặc định — nút
 * **luôn** ra một tên dùng được và **không** chờ danh sách liên kết. Đặt nhánh
 * đúng tên ấy thì nó tự nối vào việc.
 *
 * ## `asOf` (luật #7)
 *
 * Nhận `asOf`: liên kết **tại mốc ấy** (MR theo ngày tạo, commit theo ngày commit,
 * nhánh theo lúc xuất hiện / bị xoá, quyết định của người đã có lúc đó). Đang xem
 * quá khứ thì mọi thao tác ghi (gắn, ẩn, bỏ ẩn) ẩn đi — chúng tác động lên "bây
 * giờ". ⚠️ Không dựng lại được: một quyết định đã bị **gỡ** thì không còn ở mốc
 * nào (xem `GitTargetLinks`). Nơi mở chi tiết việc hôm nay không truyền `asOf`.
 * ⚠️ Điều kiện "đã nối Git" xét theo **hiện tại**, không theo mốc: nó là cấu hình,
 * không phải dữ liệu theo thời gian. Dự án chưa nối mà việc có liên kết tại mốc
 * ấy thì vẫn hiện ở chế độ chỉ đọc, nên không liên kết nào của quá khứ bị giấu.
 *
 * Chỗ cắm `GIT_DEVELOPMENT_SLOTS` ở dưới hàng nút: Tạo nhánh / Tạo MR
 * (`APM-GIT-k`), chip bản phát hành (`APM-GIT-i`).
 */
export function GitDevelopmentBlock({
  projectId,
  target,
  subject,
  asOf,
  className,
  description,
  collapsible = false,
}: {
  readonly projectId: string;
  readonly target: GitTargetRef;
  /** Việc này ở dạng `buildBranchName` cần — `branchSubjectOfTask` / `branchSubjectOfIssue`. */
  readonly subject: GitBranchSubject;
  readonly asOf?: string | undefined;
  readonly className?: string | undefined;
  /** Mô tả của việc — chuyển nguyên cho chỗ cắm (bảng tiêu chí của `APM-GIT-n`). */
  readonly description?: string | null | undefined;
  /**
   * Tiêu đề bấm để mở, mặc định gập — drawer chi tiết việc (người dùng yêu cầu
   * 2026-11-02). Điều kiện ẩn cả khối ở trên vẫn áp: không quyền / chưa nối Git
   * thì không có cả tiêu đề.
   */
  readonly collapsible?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const connection = useProjectGitConnected(projectId);
  const permissions = connection.permissions;
  const at = normalizeGitAsOf(asOf);
  const [adding, setAdding] = useState(false);
  const [showCommits, setShowCommits] = useState(false);

  const settings = useQuery({
    queryKey: gitKeys.settings(projectId),
    queryFn: () => gitApi.settings(projectId),
    // Chỉ nút sao chép cần — và nút ấy chỉ có khi dự án đã nối Git.
    enabled: connection.connected,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const links = useQuery({
    queryKey: gitKeys.links(projectId, target, at),
    queryFn: () => gitApi.links(projectId, target, at),
    // Cả khi chưa nối: liên kết đã có thì vẫn hiện, chỉ đọc (APM-GIT-q).
    enabled: permissions.read,
    retry: false,
  });

  const refresh = (): void => {
    // Chip việc trên bảng MR, nhánh, commit cũng đổi theo một lần gắn / ẩn.
    void queryClient.invalidateQueries({ queryKey: gitKeys.project(projectId) });
  };
  const hide = useMutation({
    mutationFn: (item: Subject) =>
      gitApi.addLinkOverride(projectId, {
        targetKind: target.kind,
        targetId: target.id,
        repositoryId: item.repositoryId,
        subjectKind: item.kind,
        subjectRef: item.ref,
        action: 'hide',
      }),
    onSuccess: () => {
      toast.success(t('gitTab.development.hidden'));
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const removeOverride = useMutation({
    mutationFn: (overrideId: string) => gitApi.removeLinkOverride(projectId, overrideId),
    onSuccess: () => {
      toast.success(t('gitTab.development.overrideRemoved'));
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  // Chưa biết đã nối hay chưa ⇒ không vẽ gì (không khung rỗng nháy lên rồi tắt).
  if (!permissions.read || !connection.known) return null;
  /** Dự án chưa nối Git nhưng việc đã có liên kết ⇒ chỉ đọc (APM-GIT-q). */
  const readOnly = !connection.connected;
  if (readOnly && !(links.data && hasLinks(links.data))) return null;

  const branchName = suggestedBranchName(subject, settings.data?.settings);
  const copy = async (): Promise<void> => {
    const copied = await copyText(branchName);
    if (copied) toast.success(t('gitTab.development.copied', { name: branchName }));
    else toast.error(t('gitTab.development.copyFailed', { name: branchName }));
  };

  const canAct = permissions.act && at === undefined && !readOnly;
  const busy = hide.isPending || removeOverride.isPending;

  /** Nút của một liên kết: gắn tay ⇒ "Gỡ" (xoá quyết định ấy); tự nối ⇒ "Ẩn". */
  const action = (data: GitTargetLinks, item: Subject): ReactNode => {
    if (!canAct) return null;
    const manual = data.overrides.find(
      (override) => override.action === 'add' && sameSubject(override, item),
    );
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => (manual ? removeOverride.mutate(manual.id) : hide.mutate(item))}
        title={t(manual ? 'gitTab.development.removeManualHint' : 'gitTab.development.hideHint')}
        className="shrink-0 rounded-full px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        {t(manual ? 'gitTab.development.removeManual' : 'gitTab.development.hide')}
      </button>
    );
  };

  const body = ((): ReactNode => {
    if (links.isPending) return <Skeleton className="h-10 w-full" />;
    if (links.isError) {
      // 403 (quyền vừa đổi giữa hai lần tải) ⇒ im lặng, như phần còn lại của app.
      if (isForbidden(links.error, t)) return null;
      return (
        <p role="alert" className="flex flex-wrap items-center gap-2 text-xs text-destructive">
          {errorMessage(links.error, t)}
          <button type="button" className="underline" onClick={() => void links.refetch()}>
            {t('common.retry')}
          </button>
        </p>
      );
    }

    const data = links.data;
    const hiddenOverrides = data.overrides.filter((override) => override.action === 'hide');
    const empty =
      data.branches.length === 0 && data.changeRequests.length === 0 && data.commitCount === 0;

    return (
      <>
        <GitTimeNotice meta={data} />

        {empty && !readOnly ? (
          <p className="text-xs text-muted-foreground" data-testid="git-development-empty">
            {t('gitTab.development.empty')}
          </p>
        ) : null}

        {data.changeRequests.length > 0 ? (
          <ul className="flex flex-col gap-1.5" aria-label={t('gitTab.development.changeRequests')}>
            {data.changeRequests.map((item) => (
              <li
                key={item.id}
                data-testid="git-development-cr"
                className="flex min-w-0 flex-col gap-1 rounded-lg border border-border px-2 py-1.5"
              >
                <span className="flex min-w-0 items-start justify-between gap-2">
                  <GitExternalLink url={item.webUrl} className="min-w-0 break-words text-sm">
                    <span className="mr-1.5 font-mono text-xs text-muted-foreground">
                      {changeRequestRefOfUrl(item.webUrl, item.number)}
                    </span>
                    {item.title}
                  </GitExternalLink>
                  {action(data, {
                    repositoryId: item.repositoryId,
                    kind: 'change_request',
                    ref: String(item.number),
                  })}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <GitChangeRequestBadge changeRequest={item} />
                  <GitChangeRequestMarks projectId={projectId} changeRequest={item} asOf={at} />
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {data.branches.length > 0 ? (
          <ul className="flex flex-col gap-1.5" aria-label={t('gitTab.development.branches')}>
            {data.branches.map((branch) => (
              <li
                key={branch.id}
                data-testid="git-development-branch"
                className="flex min-w-0 items-start justify-between gap-2 rounded-lg border border-border px-2 py-1.5"
              >
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <GitBranch aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="break-all font-mono text-xs">{branch.name}</span>
                  {branch.isMerged ? (
                    <span className="text-[11px] text-muted-foreground">
                      {t('gitTab.branches.flag.merged')}
                    </span>
                  ) : null}
                  {branch.deletedAt !== null ? (
                    <span className="text-[11px] text-muted-foreground">
                      {t('gitTab.branches.flag.deleted')}
                    </span>
                  ) : null}
                </span>
                {action(data, {
                  repositoryId: branch.repositoryId,
                  kind: 'branch',
                  ref: branch.name,
                })}
              </li>
            ))}
          </ul>
        ) : null}

        {data.commitCount > 0 ? (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              aria-expanded={showCommits}
              onClick={() => setShowCommits((open) => !open)}
              className="self-start text-xs text-primary underline-offset-2 hover:underline"
              data-testid="git-development-commit-count"
            >
              {t('gitTab.development.commitCount', { n: data.commitCount })}
            </button>
            {showCommits ? (
              <>
                <ul className="flex flex-col gap-1">
                  {data.commits.map((commit) => (
                    <li
                      key={`${commit.repositoryId}:${commit.sha}`}
                      data-testid="git-development-commit"
                      className="flex min-w-0 items-start justify-between gap-2 text-xs"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="break-words">{commit.title}</span>
                        <span className="text-[11px] text-muted-foreground">
                          <GitExternalLink url={commit.webUrl} className="font-mono">
                            {shortSha(commit.sha)}
                          </GitExternalLink>{' '}
                          · {displayDateTime(commit.committedAt)}
                        </span>
                      </span>
                      {action(data, {
                        repositoryId: commit.repositoryId,
                        kind: 'commit',
                        ref: commit.sha,
                      })}
                    </li>
                  ))}
                </ul>
                {data.commitCount > data.commits.length ? (
                  <p className="text-[11px] text-muted-foreground">
                    {t('gitTab.development.commitsCapped', { n: data.commits.length })}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        {hiddenOverrides.length > 0 ? (
          <ul
            className="flex flex-col gap-1 text-[11px] text-muted-foreground"
            aria-label={t('gitTab.development.hiddenTitle')}
          >
            {hiddenOverrides.map((override) => (
              <li
                key={override.id}
                data-testid="git-development-hidden"
                className="flex min-w-0 flex-wrap items-center gap-2"
              >
                <span className="min-w-0 break-all">
                  {t('gitTab.development.hiddenItem', {
                    kind: t(`gitTab.development.manual.kindOption.${override.subjectKind}`),
                    ref:
                      override.subjectKind === 'commit'
                        ? shortSha(override.subjectRef)
                        : override.subjectRef,
                  })}
                </span>
                {canAct ? (
                  <button
                    type="button"
                    className="underline"
                    disabled={busy}
                    onClick={() => removeOverride.mutate(override.id)}
                  >
                    {t('gitTab.development.unhide')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {readOnly
          ? null
          : GIT_DEVELOPMENT_SLOTS.map((Slot, index) => (
              <Slot
                key={index}
                projectId={projectId}
                target={target}
                branchName={branchName}
                links={data}
                asOf={at}
                permissions={permissions}
                description={description}
              />
            ))}
      </>
    );
  })();

  const content = (
    <>
      {readOnly ? (
        <p className="text-xs text-muted-foreground" data-testid="git-development-read-only">
          {t('gitTab.development.readOnlyNotice')}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void copy()}
              title={branchName}
            >
              <Copy aria-hidden />
              {t('gitTab.development.copyBranch')}
            </Button>
            {canAct && !adding ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(true)}>
                {t('gitTab.development.manual.open')}
              </Button>
            ) : null}
          </div>
          <code
            className="break-all rounded-lg bg-muted/60 px-2 py-1 font-mono text-[11px]"
            data-testid="git-development-branch-name"
          >
            {branchName}
          </code>
        </>
      )}

      {adding && canAct ? (
        <GitManualLinkForm projectId={projectId} target={target} onDone={() => setAdding(false)} />
      ) : null}

      {body}
    </>
  );

  if (collapsible) {
    return (
      <CollapsibleSection
        title={t('gitTab.development.title')}
        testId="git-development"
        className={className}
        bodyClassName="flex min-w-0 flex-col gap-2"
      >
        {content}
      </CollapsibleSection>
    );
  }

  return (
    <section
      aria-label={t('gitTab.development.title')}
      data-testid="git-development"
      className={cn('flex min-w-0 flex-col gap-2', className)}
    >
      <h3 className="text-xs font-medium text-muted-foreground">{t('gitTab.development.title')}</h3>
      {content}
    </section>
  );
}
